/**
 * Global App Store using Zustand
 * Manages networks, runner state, and UI state
 *
 * --- Execution registry (Phase A) ---
 * ``executions`` is a client-side projection of backend Step Functions state,
 * keyed by ``taskId``. Every widget that needs to know the live status of an
 * execution (Run-tab status widget, Build-tab execution list, future
 * AgenticNetworkRoom) should consume it via the selector hooks at the bottom
 * of this file rather than duplicating its own status-tracking state.
 *
 * Sources of truth, in precedence order:
 * 1. Live SFN lifecycle events on the AppSync agent channel
 *    (``EXECUTION_STARTED`` / ``EXECUTION_COMPLETED`` / ``EXECUTION_FAILED`` /
 *    ``SFN_TRANSITION``, plus ``APPROVAL_REQUESTED`` / ``APPROVAL_RESOLVED``)
 *    — fan-in handled by ``hooks/useExecutionLifecycle.js``, mounted once
 *    at ``WorkbenchShell``.
 * 2. REST rehydration on widget mount (``GET /networks/{id}/executions``).
 * 3. Optimistic seed from ``POST /networks/{id}/execute`` response
 *    (RunnerView seeds ``status: 'RUNNING'`` until SFN confirms).
 *
 * The registry is additive — existing ``runnerStatus`` / ``runnerOutput``
 * fields remain untouched in Phase A so adoption can be incremental.
 */
import { create } from 'zustand';
import { dedupeTraceRows } from '../utils/traceModel';

/**
 * @typedef {Object} RunnerLogEntry
 * @property {number} id
 * @property {string} [timestamp]
 * @property {string} [message]
 * @property {string} [level]
 * @property {string} [taskId]
 * @property {string} [agentId]
 * @property {string|null} [eventType]
 * @property {string|null} [component]
 * @property {Record<string, unknown>} [data]
 * @property {string} [source] live | s3 | ui
 * @property {boolean} [isBootstrap] client-only / non-trace lines
 */

function normalizeRunnerLogInput(log, nextSeq) {
  if (typeof log === 'string') {
    return {
      id: nextSeq,
      timestamp: new Date().toISOString(),
      message: log,
      level: 'INFO',
      taskId: undefined,
      agentId: undefined,
      eventType: null,
      component: null,
      data: undefined,
      source: 'ui',
      isBootstrap: true
    };
  }
  const hasStructure = Boolean(log.eventType || log.event_type || log.component || log.data);
  const isBootstrap =
    log.isBootstrap !== undefined && log.isBootstrap !== null
      ? Boolean(log.isBootstrap)
      : !hasStructure;

  return {
    id: nextSeq,
    timestamp: log.timestamp || new Date().toISOString(),
    message: log.message != null ? String(log.message) : '',
    level: log.level || 'INFO',
    taskId: log.taskId,
    agentId: log.agentId ?? log.agent_id,
    eventType: log.eventType ?? log.event_type ?? null,
    component: log.component ?? null,
    data:
      log.data != null && typeof log.data === 'object' && !Array.isArray(log.data)
        ? log.data
        : log.data != null
          ? { value: log.data }
          : undefined,
    source: log.source || 'live',
    isBootstrap
  };
}

/**
 * Execution status taxonomy. These are the only values any UI widget should
 * branch on; the lifecycle listener maps every inbound SFN/approval envelope
 * onto this closed set so downstream selectors stay stable.
 *
 * @typedef {'RUNNING' | 'AWAITING_APPROVAL' | 'SUCCEEDED' | 'FAILED' | 'TIMED_OUT' | 'ABORTED' | 'UNKNOWN'} ExecutionStatus
 */

/** Statuses where the SFN is definitively done and will not transition further. */
export const TERMINAL_EXECUTION_STATUSES = new Set([
  'SUCCEEDED',
  'FAILED',
  'TIMED_OUT',
  'ABORTED',
]);

/**
 * @typedef {Object} ExecutionRecord
 * @property {string} taskId                  SFN execution name (= server-side taskId)
 * @property {string} networkId
 * @property {string} [poolId]                runtime pool; 'default' for Workbench test runs
 * @property {string} [executionArn]
 * @property {ExecutionStatus} status
 * @property {string} [startedAt]             ISO timestamp
 * @property {string} [endedAt]               ISO timestamp on terminal state
 * @property {string} [startedBy]             cognito sub of the kick-off user
 * @property {string} [error]                 SFN execution error string
 * @property {string} [cause]                 SFN execution cause string
 * @property {string} [source]                'live' | 'rest' | 'optimistic'
 * @property {string} [updatedAt]             last state-change ISO timestamp
 */

/**
 * Precedence rule used by ``upsertExecution`` / ``setExecutionStatus``: once
 * the registry has seen a terminal status for a taskId, subsequent out-of-
 * order events (e.g. a late-arriving SFN_TRANSITION RUNNING) must not
 * reanimate it. Live SFN envelopes can arrive slightly out of order on
 * reconnect — this keeps the UI from flickering.
 */
function shouldAcceptStatusTransition(prevStatus, nextStatus) {
  if (!prevStatus) return true;
  if (prevStatus === nextStatus) return true;
  if (TERMINAL_EXECUTION_STATUSES.has(prevStatus)) {
    // Already terminal — only another terminal status of the same kind is a no-op;
    // anything else is ignored (late/reordered event).
    return false;
  }
  return true;
}

export const useAppStore = create((set) => ({
  // Networks
  networks: [],
  currentNetwork: null,
  networksLoading: false,

  // Runner state
  runnerStatus: 'idle', // 'idle' | 'uploading' | 'processing' | 'completed' | 'error'
  runnerLogs: [],
  runnerOutput: null,
  runnerProgress: 0,
  /** Monotonic id for stable console row keys */
  runnerLogSeq: 0,

  /**
   * Execution registry — see file-level docstring. Stored as a plain object
   * keyed by ``taskId`` so selectors can do O(1) lookups without Map ergonomics.
   * @type {Record<string, ExecutionRecord>}
   */
  executions: {},

  // WebSocket connectionId - shared for Run tab log streaming (set by BuildTab on connection_established)
  connectionId: null,

  // Actions
  setNetworks: (networks) => set({ networks }),
  setCurrentNetwork: (network) => set({ currentNetwork: network }),
  setNetworksLoading: (loading) => set({ networksLoading: loading }),

  // Runner actions
  setRunnerStatus: (status) => set({ runnerStatus: status }),
  addRunnerLog: (log) =>
    set((state) => {
      const nextSeq = state.runnerLogSeq + 1;
      const entry = normalizeRunnerLogInput(log, nextSeq);
      return { runnerLogs: [...state.runnerLogs, entry], runnerLogSeq: nextSeq };
    }),
  /**
   * Append structured rows from S3 with deduplication (e.g. avoid double-counting streamed + flushed logs).
   * @param {Array<Record<string, unknown>>} rows
   */
  appendTraceFromS3: (rows) =>
    set((state) => {
      if (!rows?.length) return state;
      const candidates = rows.map((row) =>
        normalizeRunnerLogInput({ ...row, source: 's3', isBootstrap: false }, 0)
      );
      const deduped = dedupeTraceRows(state.runnerLogs, candidates);
      if (deduped.length === 0) return state;
      let seq = state.runnerLogSeq;
      const stamped = deduped.map((e) => ({ ...e, id: ++seq }));
      return {
        runnerLogs: [...state.runnerLogs, ...stamped],
        runnerLogSeq: seq
      };
    }),
  clearRunnerLogs: () => set({ runnerLogs: [] }),
  setRunnerOutput: (output) => set({ runnerOutput: output }),
  setRunnerProgress: (progress) => set({ runnerProgress: progress }),
  resetRunner: () =>
    set({
      runnerStatus: 'idle',
      runnerLogs: [],
      runnerOutput: null,
      runnerProgress: 0,
      runnerLogSeq: 0
    }),
  setConnectionId: (connectionId) => {
    const prev = useAppStore.getState().connectionId;
    const label = connectionId == null ? 'NULL' : String(connectionId).slice(0, 12) + '…';
    const prevLabel = prev == null ? 'NULL' : String(prev).slice(0, 12) + '…';
    console.log(
      `[StepFlow][ConnectionId] store setConnectionId: ${prevLabel} → ${label}`,
      { prev: prev ?? null, next: connectionId ?? null }
    );
    set({ connectionId });
  },

  // Network CRUD
  addNetwork: (network) =>
    set((state) => ({
      networks: [network, ...state.networks]
    })),
  updateNetwork: (networkId, updates) =>
    set((state) => ({
      networks: state.networks.map((n) => (n.networkId === networkId ? { ...n, ...updates } : n)),
      currentNetwork:
        state.currentNetwork?.networkId === networkId
          ? { ...state.currentNetwork, ...updates }
          : state.currentNetwork
    })),
  deleteNetwork: (networkId) =>
    set((state) => ({
      networks: state.networks.filter((n) => n.networkId !== networkId),
      currentNetwork: state.currentNetwork?.networkId === networkId ? null : state.currentNetwork
    })),

  // ───── Execution registry actions ─────────────────────────────────────────
  /**
   * Upsert an execution record. Used for:
   *   - Optimistic seeding from POST /execute response (``source: 'optimistic'``)
   *   - REST rehydration from GET /networks/{id}/executions (``source: 'rest'``)
   *   - Live fan-in from the lifecycle listener (``source: 'live'``)
   *
   * Applies the terminal-state guard so a late ``SFN_TRANSITION RUNNING`` can't
   * reanimate an already-SUCCEEDED row after a reconnect replay.
   * @param {Partial<ExecutionRecord> & { taskId: string }} input
   */
  upsertExecution: (input) =>
    set((state) => {
      if (!input || !input.taskId) return state;
      const prev = state.executions[input.taskId];
      const nextStatus = input.status || prev?.status || 'UNKNOWN';
      if (prev && !shouldAcceptStatusTransition(prev.status, nextStatus)) {
        // Keep existing row but still merge non-status metadata (e.g. executionArn
        // showing up later on REST rehydration). Status itself is pinned.
        const merged = { ...prev, ...input, status: prev.status };
        if (merged === prev) return state;
        return { executions: { ...state.executions, [input.taskId]: merged } };
      }
      const next = {
        ...prev,
        ...input,
        status: nextStatus,
        updatedAt: input.updatedAt || new Date().toISOString(),
      };
      return { executions: { ...state.executions, [input.taskId]: next } };
    }),

  /**
   * Explicit status update — used by the lifecycle listener when it has only a
   * taskId + status + optional error/cause. Thin wrapper over upsertExecution
   * to keep call sites readable.
   * @param {string} taskId
   * @param {ExecutionStatus} status
   * @param {Partial<ExecutionRecord>} [extras]
   */
  setExecutionStatus: (taskId, status, extras = {}) =>
    set((state) => {
      if (!taskId) return state;
      const prev = state.executions[taskId];
      if (prev && !shouldAcceptStatusTransition(prev.status, status)) {
        // Terminal already recorded; ignore regressive transitions.
        return state;
      }
      const endedAt =
        TERMINAL_EXECUTION_STATUSES.has(status)
          ? (extras.endedAt || prev?.endedAt || new Date().toISOString())
          : prev?.endedAt;
      const next = {
        ...(prev || { taskId }),
        ...extras,
        taskId,
        status,
        endedAt,
        updatedAt: new Date().toISOString(),
      };
      return { executions: { ...state.executions, [taskId]: next } };
    }),

  /**
   * Remove all execution rows for a given network. Used when the network is
   * deleted so the registry doesn't accumulate orphaned rows. Widgets should
   * prefer ``upsertExecution`` over ``clearExecutionsForNetwork`` for normal
   * lifecycle — status is a monotonic journal, not a UI-scoped scratchpad.
   */
  clearExecutionsForNetwork: (networkId) =>
    set((state) => {
      if (!networkId) return state;
      const next = {};
      let changed = false;
      for (const [k, v] of Object.entries(state.executions)) {
        if (v.networkId === networkId) {
          changed = true;
          continue;
        }
        next[k] = v;
      }
      return changed ? { executions: next } : state;
    }),
}));

// ───── Execution registry selector hooks ────────────────────────────────────
// These are the canonical read paths. Keep UI widgets on these rather than
// reaching into ``useAppStore((s) => s.executions)`` directly — it keeps
// memoisation consistent and makes future shape changes contained to this file.

/**
 * Live record for one execution, or ``undefined`` before the first event.
 * @param {string | null | undefined} taskId
 * @returns {ExecutionRecord | undefined}
 */
export function useExecution(taskId) {
  return useAppStore((s) => (taskId ? s.executions[taskId] : undefined));
}

/**
 * All executions known for ``networkId``, newest-first by ``startedAt``.
 * Returns a fresh array each call — fine for our scale (few rows per network).
 * @param {string | null | undefined} networkId
 * @returns {ExecutionRecord[]}
 */
export function useExecutionsForNetwork(networkId) {
  return useAppStore((s) => {
    if (!networkId) return [];
    const out = [];
    for (const exec of Object.values(s.executions)) {
      if (exec.networkId === networkId) out.push(exec);
    }
    out.sort((a, b) => String(b.startedAt || '').localeCompare(String(a.startedAt || '')));
    return out;
  });
}

/**
 * Most-recent non-terminal execution for a network, or the latest terminal one
 * if none are running. This is what the Run-tab status widget consumes.
 * @param {string | null | undefined} networkId
 * @returns {ExecutionRecord | undefined}
 */
export function useActiveExecution(networkId) {
  return useAppStore((s) => {
    if (!networkId) return undefined;
    let running;
    let latestTerminal;
    for (const exec of Object.values(s.executions)) {
      if (exec.networkId !== networkId) continue;
      if (!TERMINAL_EXECUTION_STATUSES.has(exec.status)) {
        if (!running || String(exec.startedAt || '') > String(running.startedAt || '')) {
          running = exec;
        }
      } else if (
        !latestTerminal
        || String(exec.startedAt || '') > String(latestTerminal.startedAt || '')
      ) {
        latestTerminal = exec;
      }
    }
    return running || latestTerminal;
  });
}

/**
 * Convenience boolean for left-nav "currently running" indicators.
 * @param {string | null | undefined} networkId
 */
export function useIsNetworkRunning(networkId) {
  return useAppStore((s) => {
    if (!networkId) return false;
    for (const exec of Object.values(s.executions)) {
      if (exec.networkId !== networkId) continue;
      if (!TERMINAL_EXECUTION_STATUSES.has(exec.status)) return true;
    }
    return false;
  });
}
